<script>
import Card from '@/online_booking/new/components/common/Card';
import StepCounter from '@/online_booking/new/components/common/StepCounter';
import TagsList from "@/system/components/global/tags/TagsList";
import { mapState, mapActions, mapGetters } from 'vuex';
import { bookingKeys } from '@/online_booking/new/helpers/constants';

export default {
    props: {
        event: {
            type: Object,
            default: () => {}
        },

        isEventGroup: {
            type: Boolean,
            default: false
        },

        requiredLogin: {
            type: Boolean,
            default: false
        }
    },

    mounted() {
        this.setCurrentPage();

        this.setSelectedValue({
            key: bookingKeys.EVENT_INFO,
            value: this.event.name
        });
    },

    computed: {
        ...mapState('OnlineBooking', ['currentStep']),
        ...mapGetters('OnlineBooking', ['currentStepNumber', 'totalStepCount', 'getPreviousStepId']),

        isMultiDayEvent () {
            const start = moment(this.event.start);
            const end = moment(this.event.end);
            return !start.isSame(end, 'day');
        },

        isCurrentStepActive () {
            return this.currentStep === 'eventInfo';
        },

        eventNotesFormatted () {
            // notes_formatted in case of normal event
            // description_formatted in case of event group
            return this.isEventGroup ? this.event.description_formatted : this.event.notes_formatted;
        },

        differentEndYear () {
            return moment(this.event.end).year() !== moment(this.event.start).year();
        },

        firstEvent () {
            // only for event groups
            return this.event.first_event;
        },

        hideTimes () {
            return this.isEventGroup && this.isMultiDayEvent && !this.event.has_same_start_and_end_time;
        },
    },

    methods: {
        ...mapActions('OnlineBooking', ['setCurrentStep', 'setSelectedValue']),

        goToFillInformation () {
            this.setCurrentStep('fillInformation');
            if (this.requiredLogin) {
                this.$router.push({ query: { redirect: '1' } });
            }
        },

        setCurrentPage () {
            /* if there is a redirect query param, we need to set the current step to fillInformation because
             * the user is coming from the login page else we set it to eventInfo (which is the main event page)
            */
            if (this.$route.query.redirect) {
                this.setCurrentStep('fillInformation');
                this.$nextTick(() => {
                    this.$router.replace({ query: {} });
                });
            } else {
                this.setCurrentStep('eventInfo');
            }
        }
    },

    components: {
        Card,
        StepCounter,
        TagsList,
    },
}
</script>


<template>
    <div class="event-card-wrapper" v-if="isCurrentStepActive">
        <card class="grey-text event-card p-0 flex flex-column rounded-lg" :class="{'no-spaces': event.is_filled || !event.is_open}">
            <h4 class="section-title">
                <step-counter :current="currentStepNumber" :total="totalStepCount" />
                <span v-if="isEventGroup">{{ trans('online_booking.events.title2') }}</span>
                <span v-else>{{ trans('online_booking.events.title') }}</span>
            </h4>
            <!-- todo: what to do if event is already subscribed -->
            <div class="event-attending" v-if="event.client_is_subscribed">
                <i class="zmdi zmdi-check"></i>
                {{ trans('events.already_subscribed_label') }}
            </div>
            <!-- todo: what to do if the client is already in waiting list -->
            <div class="event-attending" v-if="event.client_is_waiting_list">
                <i class="zmdi zmdi-check"></i>
                {{ trans('events.already_waiting_list_label') }}
            </div>

            <div class="event-container p-6">
                <div class="row" :class="{' space-y-6 md:space-y-0': event.has_image}">
                    <div class="col-md-12">
                        <div class="events-content-container">
                            <div class="event-content">
                                <div class="event-date gap-6 bg-grey-100 rounded-lg p-6 flex-wrap">
                                    <div class="event-dates">
                                        <div class="event-day-month n-color-text-weak">
                                            <div class="event-day">{{ $filters.humanize_day(event.start)}}</div>
                                            <div class="event-month">
                                                {{ $filters.humanize_month_short(event.start) }}
                                                <span v-if="differentEndYear">{{ $filters.capitalize($filters.humanize_year_short(event.start)) }}</span>
                                            </div>
                                        </div>

                                        <span v-if="isMultiDayEvent" class="event-date-spacer n-color-text-weak">—</span>

                                        <div class="event-day-month n-color-text-weak" v-if="isMultiDayEvent">
                                            <div class="event-day">{{ $filters.humanize_day(event.end) }}</div>
                                            <div class="event-month">
                                                {{ $filters.humanize_month_short(event.end) }}
                                                <span v-if="differentEndYear">{{ $filters.capitalize($filters.humanize_year_short(event.end)) }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="event-times n-color-text-weak leading-relaxed">
                                        <span :class="{'block': !isMultiDayEvent}">{{ $filters.humanize_day_of_week(event.start) }}</span>

                                        <span class="event-time" v-show="!hideTimes">{{ $filters.lowercase($filters.humanize_time(event.start)) }}</span>

                                        <span v-if="!isMultiDayEvent">
                                            - <span class="event-time">{{ $filters.lowercase($filters.humanize_time(event.end)) }}</span>
                                        </span>

                                        <span class="event-end" v-else>
                                            <span>{{ $filters.humanize_day_of_week(event.end) }}</span>
                                            <span class="event-time" v-show="!hideTimes">{{ $filters.lowercase($filters.humanize_time(event.end)) }} </span>
                                        </span>
                                    </div>

                                    <div class="flex-grow-1 text-right">
                                        <ep-button
                                            @click.prevent="goToFillInformation"
                                            class="w-100 xs:w-auto"
                                            end-icon="arrow-right"
                                        >
                                            {{ trans('global.continue') }}
                                        </ep-button>
                                    </div>
                                </div>

                                <h3 class="text-xl n-color-text font-bold">{{ event.name }}</h3>

                                <tags-list :tags="event.tags" theme="client-universe" class="event-tags mb-5" />

                                <img v-if="event.image_url" :src="event.image_url" alt="event-image" class="w-full rounded-lg mb-3">
                                <!-- This is for single event course-->
                                <div class="event-course n-color-text mt-3" v-if="isEventGroup">
                                    <b v-if="event.events_count > 0">
                                        {{ $filters.capitalize(trans('event_groups.hardcoded_label')) }} -
                                        <span>{{ event.events_count }}</span>
                                        {{ $filters.lowercase(trans('event_groups.sessions')) }}
                                        <span v-if="firstEvent && firstEvent.practitioners && firstEvent.practitioners.length > 0"> - </span>
                                    </b>
                                    <span v-if="firstEvent && firstEvent.practitioners">
                                        <b v-for="practitioner in firstEvent.practitioners" :key="practitioner.id">{{ practitioner.realname }}</b>
                                    </span>
                                </div>

                                <!-- This is for single event-->
                                <div class="event-course n-color-text mt-3" v-else>
                                    <span v-for="practitioner in event.practitioners" :key="practitioner.id">{{ practitioner.realname }}</span>
                                </div>

                                <p class="mb-0 n-color-text-weak mt-3" v-if="eventNotesFormatted" v-sanitize-html="eventNotesFormatted"></p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="event-footer">
                <div class="event-price n-color-text" v-if="event.price">
                    {{ event.price_formatted_with_vat }}
                </div>

                <div class="event-spots n-color-text" v-if="!event.hide_spots && event.is_open">
                        <span v-if="event.available_spots == 1">
                            <span>{{ event.available_spots }}</span>
                            {{ trans('events.spot_left') }}
                        </span>
                    <span v-else>{{ event.available_spots }} {{ trans('events.spots_left') }}</span>
                </div>
                <!-- For event group/course -->
                <template v-if="isEventGroup">
                    <div class="event-address n-color-text" v-if="event.events_count > 0 && firstEvent && (firstEvent.address || firstEvent.city || firstEvent.zip)">
                        <a target="_blank" :href="'http://maps.google.com/?q=' + firstEvent.address + ',' + firstEvent.zip + ',' + firstEvent.city" style="color: #797979;">
                            <span v-if="firstEvent.address">{{ firstEvent.address }}<span v-if="firstEvent.city || firstEvent.zip">, </span></span>
                            <span v-if="firstEvent.zip">{{ firstEvent.zip }}<span v-if="firstEvent.city">, </span></span>
                            <span v-if="firstEvent.city">{{ firstEvent.city }}</span>
                        </a>
                    </div>
                </template>

                <!-- For single event -->
                <template v-else>
                    <div class="event-address n-color-text" v-if="event.address || event.city || event.zip">
                        <a class="n-color-text-weak hover:text-grey-800 transition" target="_blank" :href="'http://maps.google.com/?q=' + event.address + ',' + event.zip + ',' + event.city">
                            <span v-if="event.address">{{ event.address }}<span v-if="event.city || event.zip">, </span></span>
                            <span v-if="event.zip">{{ event.zip }}<span v-if="event.city">, </span></span> 
                            <span v-if="event.city">{{ event.city }}</span>
                        </a>
                    </div>
                </template>
            </div>
        </card>

        <card class="grey-text event-card p-0 flex flex-column rounded-lg" v-if="isEventGroup">
            <h4 class="section-title">
                {{ trans('event_groups.sessions') }}
            </h4>
            <div class="flex flex-col space-y-6 p-6">
                <div :key="item.id" v-for="(item, index) in event.events">
                    <h5 class="header-title mt-0">{{ index + 1 }}. {{ item.name }}</h5>
                    <p class="mb-0">{{ item.interval_formatted }}</p>
                    <span
                        class="text-muted event-notes mt-3"
                        v-if="item.notes_formatted"
                        v-sanitize-html="item.notes_formatted">
                    </span>
                </div>

                <!-- if there are sessions, the "continue to next step" button appears below the sessions also -->
                <button
                    @click.prevent="goToFillInformation"
                    class="btn btn-custom mt-6 self-end"
                >
                    <span>{{ trans('global.continue') }}</span>
                    <i class="zmdi zmdi-arrow-right ml-3"></i>
                </button>
            </div>
        </card>
    </div>
</template>

<style lang="scss" scoped>
.event-card {
    flex-wrap: nowrap;
}
</style>
